/**
 * /bookkeeping — manual transaction log + charts.
 *
 * The "books" view: a list of transactions you've recorded, totals
 * across the period, charts for monthly trend + category breakdown +
 * YoY comparison, and a sticky "Add transaction" form for capturing
 * cash items that have no document (maid salary, flowers, daily milk).
 *
 * P1+P2 shipped the table + manual entry. P3 added auto-link from the
 * doc review dialog (handled in ReviewDialog itself; this page just
 * lists the rows it created). P4 (this commit) adds:
 *   • range selector — Month / 3M / 12M / This year / Last year / All
 *   • monthly trend chart — income vs expense bars (last 12 months)
 *   • expense-by-category pie
 *   • YoY comparison line — this year vs last year, monthly
 *   • subcategory hint chips on the Add form (medical bills covered
 *     under Health: Doctor / Pharmacy / Lab / Hospital / Dental / etc.)
 *
 * Charts use recharts (already in deps). Aggregations all happen
 * client-side — we fetch the last 24 months in one query and slice
 * locally so swapping ranges is instant.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Wallet,
  TrendingUp,
  TrendingDown,
  Loader2,
  Calendar,
  BarChart3,
  PieChart as PieChartIcon,
  LineChart as LineChartIcon,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  listTransactions,
  createTransaction,
  deleteTransaction,
  rupeesToPaise,
  paiseToRupees,
  formatRupees,
  EXPENSE_CATEGORIES,
  INCOME_CATEGORIES,
  type Transaction,
} from "@/lib/api";
import { SUBCATEGORY_HINTS } from "@/lib/folder-to-bookkeeping";
import { queryKeys } from "@/lib/queryKeys";

export const Route = createFileRoute("/_app/bookkeeping")({
  component: BookkeepingView,
});

// ──────────────────────────────────────────────────────────────────────
// Date helpers — kept tiny and UTC-only to avoid timezone drift.
// ──────────────────────────────────────────────────────────────────────

function todayISO(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

function isoFromUTC(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function lastDayOfMonth(y: number, m1to12: number): number {
  return new Date(Date.UTC(y, m1to12, 0)).getUTCDate();
}

type RangeKey =
  | "month"
  | "last3"
  | "last12"
  | "thisYear"
  | "lastYear"
  | "all";

interface DateRange {
  from: string | null; // null = no lower bound (used by "all")
  to: string | null;
  label: string;
}

function computeRange(kind: RangeKey): DateRange {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  const d = now.getUTCDate();
  switch (kind) {
    case "month": {
      const from = isoFromUTC(y, m, 1);
      const to = isoFromUTC(y, m, lastDayOfMonth(y, m));
      const label = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      });
      return { from, to, label };
    }
    case "last3": {
      // 3 calendar months back (inclusive of current).
      const startY = m - 2 < 1 ? y - 1 : y;
      const startM = ((m - 2 - 1 + 12) % 12) + 1;
      const from = isoFromUTC(startY, startM, 1);
      const to = isoFromUTC(y, m, d);
      return { from, to, label: "Last 3 months" };
    }
    case "last12": {
      // 12 calendar months back inclusive.
      const startY = m - 11 < 1 ? y - 1 : y;
      const startM = ((m - 11 - 1 + 12) % 12) + 1;
      const from = isoFromUTC(startY, startM, 1);
      const to = isoFromUTC(y, m, d);
      return { from, to, label: "Last 12 months" };
    }
    case "thisYear": {
      const from = isoFromUTC(y, 1, 1);
      const to = isoFromUTC(y, 12, 31);
      return { from, to, label: `${y}` };
    }
    case "lastYear": {
      const from = isoFromUTC(y - 1, 1, 1);
      const to = isoFromUTC(y - 1, 12, 31);
      return { from, to, label: `${y - 1}` };
    }
    case "all": {
      return { from: null, to: null, label: "All time" };
    }
  }
}

// ──────────────────────────────────────────────────────────────────────
// Chart palette — tied to category for stable colours across charts.
// ──────────────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  Food: "#f59e0b",
  Bills: "#3b82f6",
  Housekeeping: "#8b5cf6",
  Donations: "#ec4899",
  Health: "#ef4444",
  Education: "#10b981",
  Travel: "#0ea5e9",
  Shopping: "#f43f5e",
  Misc: "#94a3b8",
  Salary: "#22c55e",
  Rent: "#06b6d4",
  Interest: "#a855f7",
};

function colorFor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? "#64748b";
}

// ──────────────────────────────────────────────────────────────────────
// Main view
// ──────────────────────────────────────────────────────────────────────

function BookkeepingView() {
  const qc = useQueryClient();
  const [rangeKey, setRangeKey] = useState<RangeKey>("month");
  const range = useMemo(() => computeRange(rangeKey), [rangeKey]);

  // Primary query — drives headline totals + transaction list +
  // category breakdown. Filtered server-side to the chosen range.
  const txns = useQuery({
    queryKey: queryKeys.transactions(
      range.from || range.to
        ? { from: range.from ?? undefined, to: range.to ?? undefined }
        : undefined,
    ),
    queryFn: () =>
      listTransactions({
        from: range.from ?? undefined,
        to: range.to ?? undefined,
        limit: 500,
      }),
    staleTime: 30_000,
  });

  // Chart query — always pulls the last 24 months so the monthly
  // trend chart and YoY comparison have stable horizons regardless of
  // the active range. Independent of the primary range filter.
  const chartFrom = useMemo(() => {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth() + 1;
    const startY = m - 23 < 1 ? y - 2 : y - 1;
    const startM = ((m - 23 - 1 + 12) % 12) + 1;
    return isoFromUTC(startY, startM, 1);
  }, []);
  const chartTxns = useQuery({
    queryKey: ["transactions-chart-24m", chartFrom],
    queryFn: () => listTransactions({ from: chartFrom, limit: 500 }),
    staleTime: 60_000,
  });

  // Group transactions by category for a per-category breakdown card.
  const byCategory = useMemo(() => {
    if (!txns.data) return { income: [], expense: [] };
    const inc = new Map<string, number>();
    const exp = new Map<string, number>();
    for (const t of txns.data.transactions) {
      const target = t.kind === "income" ? inc : exp;
      target.set(t.category, (target.get(t.category) ?? 0) + t.amount_paise);
    }
    const sort = (m: Map<string, number>) =>
      Array.from(m.entries())
        .map(([category, total]) => ({ category, total }))
        .sort((a, b) => b.total - a.total);
    return { income: sort(inc), expense: sort(exp) };
  }, [txns.data]);

  // Build monthly-aggregate buckets for the last 12 months — one row
  // per (YYYY-MM) with income + expense paise. Used by both the
  // monthly bar chart and the YoY line chart.
  const monthlyAgg = useMemo(() => {
    if (!chartTxns.data) return [] as MonthlyRow[];
    return aggregateMonthly(chartTxns.data.transactions, 12);
  }, [chartTxns.data]);

  // YoY split — group the 24-month chart data by month-of-year so the
  // line chart can plot "this year" vs "last year" on the same axes.
  const yoyData = useMemo(() => {
    if (!chartTxns.data) return [] as YoYRow[];
    return aggregateYoY(chartTxns.data.transactions);
  }, [chartTxns.data]);

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-8 py-8 space-y-6">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl">Bookkeeping</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Income, expenses, the gap between them — plus charts and category
            breakdowns. Doc-backed transactions auto-link when you confirm a
            bill, donation receipt or pay slip.
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          <Select
            value={rangeKey}
            onValueChange={(v) => setRangeKey(v as RangeKey)}
          >
            <SelectTrigger className="h-8 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">{computeRange("month").label}</SelectItem>
              <SelectItem value="last3">Last 3 months</SelectItem>
              <SelectItem value="last12">Last 12 months</SelectItem>
              <SelectItem value="thisYear">{computeRange("thisYear").label}</SelectItem>
              <SelectItem value="lastYear">{computeRange("lastYear").label}</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Headline totals — three big numbers. */}
      <section className="grid grid-cols-3 gap-3 sm:gap-4">
        <TotalCard
          label="Income"
          paise={txns.data?.totals.income_paise ?? 0}
          tone="positive"
          icon={TrendingUp}
        />
        <TotalCard
          label="Expense"
          paise={txns.data?.totals.expense_paise ?? 0}
          tone="negative"
          icon={TrendingDown}
        />
        <TotalCard
          label="Balance"
          paise={txns.data?.totals.balance_paise ?? 0}
          tone={
            (txns.data?.totals.balance_paise ?? 0) >= 0
              ? "positive"
              : "negative"
          }
          icon={Wallet}
        />
      </section>

      {/* Add transaction form. */}
      <AddTransactionCard
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["transactions"] });
          qc.invalidateQueries({ queryKey: ["transactions-chart-24m"] });
        }}
      />

      {/* ── CHARTS (P4) ─────────────────────────────────────────────
          Three small cards stacked. Each only renders when there's
          enough data to be meaningful — no empty axes. */}
      {monthlyAgg.length > 0 && (
        <ChartCard
          icon={BarChart3}
          title="Monthly trend"
          hint="Income vs expense, last 12 months."
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={monthlyAgg.map((m) => ({
                month: m.label,
                Income: paiseToRupees(m.income_paise),
                Expense: paiseToRupees(m.expense_paise),
              }))}
              margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11 }}
                interval={0}
                tickFormatter={(s: string) => s.slice(0, 3)}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v: number) => `Rs ${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                contentStyle={{ fontSize: "12px" }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Bar dataKey="Income" fill={CATEGORY_COLORS.Salary} radius={[3, 3, 0, 0]} />
              <Bar dataKey="Expense" fill={CATEGORY_COLORS.Bills} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {byCategory.expense.length > 0 && (
        <ChartCard
          icon={PieChartIcon}
          title="Expense by category"
          hint={`Where the money went in ${range.label}.`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-center">
            <div className="sm:col-span-3">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={byCategory.expense.map((row) => ({
                      name: row.category,
                      value: paiseToRupees(row.total),
                    }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={75}
                    innerRadius={35}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                  >
                    {byCategory.expense.map((row, i) => (
                      <Cell
                        key={`cell-${i}`}
                        fill={colorFor(row.category)}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: number) => `Rs ${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                    contentStyle={{ fontSize: "12px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="sm:col-span-2 space-y-1 text-xs">
              {byCategory.expense.map((row) => (
                <li
                  key={row.category}
                  className="flex items-center gap-2 justify-between"
                >
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <span
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: colorFor(row.category) }}
                    />
                    <span className="truncate">{row.category}</span>
                  </span>
                  <span className="font-medium tabular-nums">
                    {formatRupees(row.total)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </ChartCard>
      )}

      {yoyData.length > 0 && (
        <ChartCard
          icon={LineChartIcon}
          title="Year on year"
          hint="This year's expenses vs last year's, by month."
        >
          <ResponsiveContainer width="100%" height={240}>
            <LineChart
              data={yoyData}
              margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v: number) => `Rs ${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                contentStyle={{ fontSize: "12px" }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Line
                type="monotone"
                dataKey="thisYear"
                name="This year"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="lastYear"
                name="Last year"
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Per-category breakdown — keep the compact list too. The pie
          shows shape, the list shows exact totals. */}
      {byCategory.income.length > 0 && (
        <section>
          <h2 className="font-display text-base mb-2">Income by category</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {byCategory.income.map((row) => (
              <li
                key={row.category}
                className="rounded-md border border-border bg-card px-3 py-2 text-sm flex justify-between"
              >
                <span>{row.category}</span>
                <span className="font-medium">{formatRupees(row.total)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Transaction list */}
      <section>
        <h2 className="font-display text-base mb-2">Transactions</h2>
        {txns.isLoading && (
          <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        )}
        {txns.data?.transactions.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No transactions in this range. Use the form above to add cash
            entries — maid salary, daily milk, taxi fare, anything.
          </div>
        )}
        {txns.data && txns.data.transactions.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {txns.data.transactions.map((t) => (
              <TransactionRow
                key={t.id}
                txn={t}
                onDeleted={() => {
                  qc.invalidateQueries({ queryKey: ["transactions"] });
                  qc.invalidateQueries({ queryKey: ["transactions-chart-24m"] });
                }}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

function TotalCard({
  label,
  paise,
  tone,
  icon: Icon,
}: {
  label: string;
  paise: number;
  tone: "positive" | "negative" | "neutral";
  icon: typeof Wallet;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div
        className={
          "mt-1 font-display text-xl sm:text-2xl tabular-nums " +
          (tone === "negative"
            ? "text-destructive"
            : tone === "positive"
              ? "text-success"
              : "text-foreground")
        }
      >
        {formatRupees(paise)}
      </div>
    </div>
  );
}

function ChartCard({
  icon: Icon,
  title,
  hint,
  children,
}: {
  icon: typeof BarChart3;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-3 sm:p-4 space-y-2">
      <div className="flex items-baseline justify-between flex-wrap gap-1">
        <h2 className="font-display text-base inline-flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </h2>
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      </div>
      {children}
    </section>
  );
}

function AddTransactionCard({ onCreated }: { onCreated: () => void }) {
  const [kind, setKind] = useState<"expense" | "income">("expense");
  const [category, setCategory] = useState<string>("Food");
  const [subcategory, setSubcategory] = useState("");
  const [amountRupees, setAmountRupees] = useState("");
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");

  const allowedCats = kind === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  // Subcategory hint chips for the chosen category — empty for Misc.
  // Medical bill subcategories live under Health (#205).
  const hints = SUBCATEGORY_HINTS[category] ?? [];

  // Reset category when switching kind (the lists are different).
  const onKindChange = (k: "income" | "expense") => {
    setKind(k);
    setCategory(k === "income" ? "Salary" : "Food");
    setSubcategory("");
  };

  const create = useMutation({
    mutationFn: () => {
      const amount_paise = rupeesToPaise(Number(amountRupees));
      return createTransaction({
        kind,
        category,
        subcategory: subcategory.trim() || null,
        amount_paise,
        txn_date: date,
        note: note.trim() || null,
      });
    },
    onSuccess: () => {
      toast.success("Transaction added");
      setAmountRupees("");
      setSubcategory("");
      setNote("");
      onCreated();
    },
    onError: (e) => {
      toast.error("Couldn't add", {
        description: e instanceof Error ? e.message : "Try again",
      });
    },
  });

  const amount = Number(amountRupees);
  const canSubmit =
    !!amountRupees && Number.isFinite(amount) && amount > 0 && !!category && !!date;

  return (
    <section className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base inline-flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add transaction
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Type</Label>
          <Select value={kind} onValueChange={(v) => onKindChange(v as any)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="expense">Expense (money out)</SelectItem>
              <SelectItem value="income">Income (money in)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Category</Label>
          <Select
            value={category}
            onValueChange={(v) => {
              setCategory(v);
              setSubcategory(""); // reset on category swap so hints feel fresh
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allowedCats.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs">Subcategory (optional)</Label>
          <Input
            value={subcategory}
            onChange={(e) => setSubcategory(e.target.value)}
            placeholder={
              category === "Health"
                ? "Tap a chip below or type one (e.g. Pharmacy)"
                : kind === "expense"
                  ? category === "Bills"
                    ? "e.g. Electricity / Gym / Yoga / School fees"
                    : category === "Housekeeping"
                      ? "e.g. Maid / Driver / Flowers"
                      : "Optional"
                  : "e.g. employer / property"
            }
            maxLength={60}
          />
          {hints.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {hints.map((h) => (
                <button
                  type="button"
                  key={h}
                  onClick={() => setSubcategory(h)}
                  className={
                    "text-[11px] px-2 py-0.5 rounded-full border transition " +
                    (subcategory === h
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground")
                  }
                >
                  {h}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Amount (Rs)</Label>
          <Input
            value={amountRupees}
            onChange={(e) => setAmountRupees(e.target.value)}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="0.00"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Date
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs">Note (optional)</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. March salary · Friday milk · Diwali tip"
            maxLength={200}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={() => create.mutate()}
          disabled={!canSubmit || create.isPending}
          className="w-full sm:w-auto"
        >
          {create.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1.5" />
              Add transaction
            </>
          )}
        </Button>
      </div>
    </section>
  );
}

function TransactionRow({
  txn,
  onDeleted,
}: {
  txn: Transaction;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const del = useMutation({
    mutationFn: () => deleteTransaction(txn.id),
    onSuccess: () => {
      toast.success("Deleted");
      onDeleted();
    },
    onError: (e) => {
      toast.error("Couldn't delete", {
        description: e instanceof Error ? e.message : "Try again",
      });
    },
  });
  const isIncome = txn.kind === "income";
  return (
    <li className="p-3 flex items-start justify-between gap-3 text-sm">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium">{txn.category}</span>
          {txn.subcategory && (
            <span className="text-muted-foreground text-xs">
              · {txn.subcategory}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground/80">
            {txn.txn_date}
          </span>
          {txn.file_id && (
            <span
              className="text-[10px] text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1"
              title="Auto-linked from a confirmed document"
            >
              · 📎 doc
            </span>
          )}
        </div>
        {txn.note && (
          <p className="text-muted-foreground text-xs mt-0.5 truncate">
            {txn.note}
          </p>
        )}
      </div>
      <div className="text-right space-y-1 shrink-0">
        <div
          className={
            "font-medium tabular-nums " +
            (isIncome ? "text-success" : "text-destructive")
          }
        >
          {isIncome ? "+" : "-"}
          {formatRupees(txn.amount_paise)}
        </div>
        {confirming ? (
          <div className="flex items-center gap-1 text-xs">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() => setConfirming(false)}
              disabled={del.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="h-7 px-2"
              onClick={() => del.mutate()}
              disabled={del.isPending}
            >
              {del.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="text-muted-foreground hover:text-destructive inline-flex items-center gap-1 text-xs"
            aria-label="Delete transaction"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Aggregation helpers (kept at module scope so they're testable later)
// ──────────────────────────────────────────────────────────────────────

interface MonthlyRow {
  ym: string;       // YYYY-MM
  label: string;    // "Jan 26"
  income_paise: number;
  expense_paise: number;
}

interface YoYRow {
  month: string;     // "Jan", "Feb", …
  thisYear: number;  // rupees
  lastYear: number;  // rupees
}

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function aggregateMonthly(txns: Transaction[], monthCount: number): MonthlyRow[] {
  const now = new Date();
  const buckets = new Map<string, MonthlyRow>();
  // Initialise empty buckets so the chart shows months even with 0 data.
  for (let i = monthCount - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const ym = `${y}-${String(m).padStart(2, "0")}`;
    buckets.set(ym, {
      ym,
      label: `${MONTH_LABELS[m - 1]} ${String(y).slice(-2)}`,
      income_paise: 0,
      expense_paise: 0,
    });
  }
  for (const t of txns) {
    const ym = t.txn_date.slice(0, 7);
    const row = buckets.get(ym);
    if (!row) continue; // out of window
    if (t.kind === "income") row.income_paise += t.amount_paise;
    else row.expense_paise += t.amount_paise;
  }
  return Array.from(buckets.values());
}

function aggregateYoY(txns: Transaction[]): YoYRow[] {
  const now = new Date();
  const thisY = now.getUTCFullYear();
  const lastY = thisY - 1;
  // 12 entries, one per month. Each accumulates expense only — YoY for
  // income is interesting but Uma's main question is "are we spending
  // more this year". Keep this surface focused.
  const rows: YoYRow[] = MONTH_LABELS.map((label) => ({
    month: label,
    thisYear: 0,
    lastYear: 0,
  }));
  for (const t of txns) {
    if (t.kind !== "expense") continue;
    const y = parseInt(t.txn_date.slice(0, 4), 10);
    const m = parseInt(t.txn_date.slice(5, 7), 10);
    if (m < 1 || m > 12) continue;
    if (y === thisY) rows[m - 1].thisYear += t.amount_paise;
    else if (y === lastY) rows[m - 1].lastYear += t.amount_paise;
  }
  // Convert to rupees for the chart axis.
  return rows.map((r) => ({
    month: r.month,
    thisYear: paiseToRupees(r.thisYear),
    lastYear: paiseToRupees(r.lastYear),
  }));
}
